import omit from "lodash/omit";
import { AdminStats } from "../../model/admin/AdminStats";
import { AdminCreatePSADto } from "../../model/admin/dto/AdminCreatePSA";
import { AdminGetPSAsResponseDto } from "../../model/admin/dto/AdminGetPSAsResponseDto";
import { AdminGetUserMessagesDto } from "../../model/admin/dto/AdminGetUserMessagesDto";
import { AdminGetUserMessagesResponseDto } from "../../model/admin/dto/AdminGetUserMessagesResponseDto";
import { AdminMessageUserDto } from "../../model/admin/dto/AdminMessageUserDto";
import { GetAdminStatsDto } from "../../model/admin/dto/GetAdminStatsDto";
import { SearchUsersDto } from "../../model/admin/dto/SearchUsersDto";
import { SearchUsersResponseDto } from "../../model/admin/dto/SearchUsersResponseDto";
import { ToggleUserStatusDto } from "../../model/admin/dto/ToggleUserStatusDto";
import { UpdateUserPlanDto } from "../../model/admin/dto/UpdateUserPlanDto";
import { UpdateUserThresholdsDto } from "../../model/admin/dto/UpdateUserThresholdsDto";
import { GenericWithUserId } from "../../model/shared/dto/GenericWithUserId";
import { AtiraApi } from "../AxiosClient";
import { AdminCreateSupportSessionDto } from "../../model/admin/dto/AdminCreateSupportSessionDto";
import { LoginResponseDto } from "../../model/user/dto/LoginResponseDto";
import isString from "lodash/isString";
import { AdminUpdatePSADto } from "../../model/admin/dto/AdminUpdatePSADto";
import { AdminDeletePSADto } from "../../model/admin/dto/AdminDeletePSADto";
import { AdminResendPSADto } from "../../model/admin/dto/AdminResendPSADto";
import { AdminCreateRoadmapItemDto } from "../../model/admin/dto/AdminCreateRoadmapItemDto";
import { AdminDeleteRoadmapItemDto } from "../../model/admin/dto/AdminDeleteRoadmapItemDto";
import { AdminGetRoadmapItemsResponseDto } from "../../model/admin/dto/AdminGetRoadmapItemsResponseDto";
import { AdminUpdateRoadmapItemDto } from "../../model/admin/dto/AdminUpdateRoadmapItemDto";

export class AdminAxios {
  static getAdminStats(dto: GetAdminStatsDto) {
    return AtiraApi.post<AdminStats>("/admin/stats", dto).then(
      ({ data }) => data,
    );
  }

  static searchUsers(dto: SearchUsersDto) {
    return AtiraApi.post<SearchUsersResponseDto>(
      `/admin/user/search`,
      dto,
    ).then(({ data }) => data);
  }

  static updateUserPlan(dto: UpdateUserPlanDto) {
    return AtiraApi.post<void>(`/user/${dto.userId}/plan/update`, dto).then(
      ({ data }) => data,
    );
  }

  static toggleUserStatus(dto: ToggleUserStatusDto) {
    return AtiraApi.post<void>(`/admin/user/${dto.userId}/toggle-status`).then(
      ({ data }) => data,
    );
  }

  static createSupportSession(dto: AdminCreateSupportSessionDto) {
    return AtiraApi.post<LoginResponseDto>(
      `/admin/user/${dto.userId}/support-session`,
    ).then(({ data }) => data);
  }

  static updateUserThresholds(dto: UpdateUserThresholdsDto) {
    return AtiraApi.post<void>(
      `/admin/user/${dto.userId}/manage-thresholds`,
      dto,
    ).then(({ data }) => data);
  }

  static sendMessageToUser(dto: AdminMessageUserDto) {
    return AtiraApi.post<void>(`/admin/user/${dto.userId}/message`, {
      message: dto.message,
    }).then(({ data }) => data);
  }

  static getAdminMessages(dto: AdminGetUserMessagesDto) {
    return AtiraApi.post<AdminGetUserMessagesResponseDto>(
      `/admin/user/${dto.userId}/get-messages`,
      dto,
    ).then(({ data }) => data);
  }

  static markAdminMessagesAsRead(dto: GenericWithUserId) {
    return AtiraApi.post<void>(
      `/admin/user/${dto.userId}/mark-messages-as-read`,
    ).then(({ data }) => data);
  }

  static getPSAs(dto: GenericWithUserId) {
    return AtiraApi.post<AdminGetPSAsResponseDto>(`/admin/psa`, dto).then(
      ({ data }) => data,
    );
  }

  static createPSA(dto: AdminCreatePSADto) {
    const formData = new FormData();

    Object.entries(omit(dto, "images")).forEach(([key, value]) => {
      formData.append(key, isString(value) ? value : JSON.stringify(value));
    });

    if (dto.images?.[0]) {
      formData.append("image", dto.images?.[0]);
    }

    return AtiraApi.post<void>(`/admin/psa/create`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then(({ data }) => data);
  }

  static updatePSA(dto: AdminUpdatePSADto) {
    const formData = new FormData();

    Object.entries(omit(dto, "images")).forEach(([key, value]) => {
      formData.append(key, isString(value) ? value : JSON.stringify(value));
    });

    const image = dto.images?.[0] as any;

    if (image instanceof File || image === null) {
      formData.append("image", image);
    }

    return AtiraApi.patch<void>(`/admin/psa/${dto.psaId}/update`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then(({ data }) => data);
  }

  static markPSAAsRead(dto: GenericWithUserId) {
    return AtiraApi.post<void>(`/admin/psa/${dto.userId}/mark-as-read`).then(
      ({ data }) => data,
    );
  }

  static deletePSA(dto: AdminDeletePSADto) {
    return AtiraApi.delete(`/admin/psa/${dto.psaId}/delete`, {
      data: dto,
    }).then(({ data }) => data);
  }

  static resendPSA(dto: AdminResendPSADto) {
    return AtiraApi.post<void>(`/admin/psa/${dto.psaId}/resend`, dto).then(
      ({ data }) => data,
    );
  }

  static getRoadmapItems(dto: GenericWithUserId) {
    return AtiraApi.post<AdminGetRoadmapItemsResponseDto>(
      `/admin/roadmap`,
      dto,
    ).then(({ data }) => data);
  }

  static createRoadmapItem(dto: AdminCreateRoadmapItemDto) {
    const formData = new FormData();

    Object.entries(omit(dto, "images")).forEach(([key, value]) => {
      formData.append(key, isString(value) ? value : JSON.stringify(value));
    });

    if (dto.images?.[0]) {
      formData.append("image", dto.images[0]);
    }

    return AtiraApi.post<void>(`/admin/roadmap/create`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then(({ data }) => data);
  }

  static updateRoadmapItem(dto: AdminUpdateRoadmapItemDto) {
    const formData = new FormData();

    Object.entries(omit(dto, "images")).forEach(([key, value]) => {
      formData.append(key, isString(value) ? value : JSON.stringify(value));
    });

    const image = dto.images?.[0] as any;

    if (image instanceof File || image === null) {
      formData.append("image", image);
    }

    return AtiraApi.patch<void>(
      `/admin/roadmap/${dto.roadmapItemId}/update`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    ).then(({ data }) => data);
  }

  static deleteRoadmapItem(dto: AdminDeleteRoadmapItemDto) {
    return AtiraApi.delete(`/admin/roadmap/${dto.roadmapItemId}/delete`, {
      data: dto,
    }).then(({ data }) => data);
  }
}
